import React from 'react'
import { Button, H1 } from '@/common/ui'
import { APP } from '@/common/strings'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { Gift } from 'lucide-react'
import RewardItem from './rewardItem'
import { REWARDS_COPY } from '@/locales/en'
import { useFetchRewards } from '../userQueries'
import useAccountStore from '@/account/accountStore'
import { usePrimaryAccount } from '@/account/usePrimaryAccount'
import { resolveHomeRouteForContext } from '@/common/navigation/accountRoutes'

function LoadingView() {
    return (
        <div className="w-full min-h-[140px] animate-pulse flex flex-col gap-3">
            <div className="h-4 bg-stone-100 rounded w-1/3" />
            <div className="h-3 bg-stone-100 rounded w-full" />
            <div className="h-3 bg-stone-100 rounded w-full" />
        </div>
    )
}

const RewardsPage: React.FC = () => {
    const navigate = useNavigate()
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const primaryAccount = usePrimaryAccount()
    const { data: rewards = [], isLoading } = useFetchRewards()
    const hasRewards = Boolean(rewards && rewards.length)
    const totalRewards = rewards.reduce((acc, r) => acc + r.quantityRemaining, 0)

    return (
        <>
            <Helmet>
                <title>
                    {REWARDS_COPY.pageTitle} | {APP.title}
                </title>
            </Helmet>
            <H1 className="mb-4 md:mb-8">{REWARDS_COPY.pageTitle}</H1>

            {isLoading ? (
                <LoadingView />
            ) : hasRewards ? (
                <div className="self-stretch p-4 rounded-2xl outline outline-1 outline-offset-[-1px] outline-border-outline flex flex-col justify-center items-start gap-4 md:gap-6 overflow-hidden">
                    <div className="self-stretch flex flex-col justify-start items-start gap-2">
                        <div className="self-stretch inline-flex justify-start items-start gap-4">
                            <Gift size={20} />
                            <div className="w-52 self-stretch justify-center text-content-primary text-n font-medium font-['Inter'] leading-5">
                                {REWARDS_COPY.freeTradesTitle}
                                {` (${totalRewards})`}
                            </div>
                        </div>
                        <div className="justify-center text-content-secondary text-xs font-normal font-['Inter'] leading-5">
                            {REWARDS_COPY.freeTradesDescription}
                        </div>
                    </div>

                    {rewards.map((r, idx) => (
                        <React.Fragment key={r.promotionId}>
                            <RewardItem reward={r} />

                            {idx < rewards.length - 1 && (
                                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider" />
                            )}
                        </React.Fragment>
                    ))}
                </div>
            ) : (
                <>
                    <div className="justify-center text-black text-xs font-normal font-['Inter'] leading-5">
                        {REWARDS_COPY.noRewards}
                    </div>

                    <Button
                        className="mt-10 flex flex-col justify-start items-start"
                        variant="secondary"
                        onClick={() =>
                            navigate(
                                resolveHomeRouteForContext({
                                    primaryAccount: primaryAccount ?? null,
                                    activeAccountId,
                                }),
                            )
                        }
                    >
                        <div className="justify-start text-content-primary text-sm font-medium font-['Inter'] leading-tight">
                            {REWARDS_COPY.noRewardsButton}
                        </div>
                    </Button>
                </>
            )}
        </>
    )
}

export default React.memo(RewardsPage)
